/**
 * Same step window as the stage auto-loop (`sequence-step-hold.helper` in event-service).
 * Dwell is the minimum. A video holds only the last step where it is visible until one play
 * finishes. Earlier steps of that video still advance on the dwell. No stored length stays on the dwell.
 */

/** A bad duration cannot pin the last step longer than this after that step was entered. */
export const SEQUENCE_VIDEO_HOLD_CAP_MS = 2 * 60 * 60 * 1000;

export type SequenceHoldVideo = {
  id: string;
  toFrame: number;
  startFrame?: number;
  loop: boolean;
  startDelayMs: number;
  durationMs?: number;
  trimStartMs?: number;
  trimFromEndMs?: number;
};

export type SequenceFrameHistory = {
  mediaKey: string;
  lastIndex?: number;
  byFrame: Map<number, number>;
  /** Dwell restart for the frame already on screen. Video stamps in `byFrame` stay put. */
  dwellRestart?: { index: number; at: number };
};

export function createSequenceFrameHistory(): SequenceFrameHistory {
  return { mediaKey: '', byFrame: new Map() };
}

/** Videos that stop being visible when this frame advances. */
export function videosCutByLeavingFrame(videos: SequenceHoldVideo[], frameIndex: number): SequenceHoldVideo[] {
  return videos.filter(
    (video) => video.startFrame != null && frameIndex >= video.startFrame && frameIndex === video.toFrame,
  );
}

export function parseSequenceHoldVideos(raw: string | undefined): SequenceHoldVideo[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object') return [];
  const { videos } = parsed as { videos?: unknown };
  if (!Array.isArray(videos)) return [];
  const holds: SequenceHoldVideo[] = [];
  for (const entry of videos) {
    if (!entry || typeof entry !== 'object') continue;
    const video = entry as {
      id?: unknown;
      toFrame?: unknown;
      startFrame?: unknown;
      loop?: unknown;
      startDelayMs?: unknown;
      durationMs?: unknown;
      trimStartMs?: unknown;
      trimFromEndMs?: unknown;
    };
    const toFrame = typeof video.toFrame === 'number' ? video.toFrame : 0;
    const startFrame = typeof video.startFrame === 'number' ? video.startFrame : undefined;
    const durationMs = typeof video.durationMs === 'number' ? video.durationMs : undefined;
    if (typeof video.id !== 'string' || !video.id || toFrame < 1) continue;
    holds.push({
      id: video.id,
      toFrame,
      ...(startFrame != null && startFrame >= 1 ? { startFrame } : {}),
      loop: video.loop === true,
      startDelayMs: nonNegative(video.startDelayMs),
      ...(durationMs != null && Number.isFinite(durationMs) && durationMs > 0 ? { durationMs } : {}),
      ...(nonNegative(video.trimStartMs) > 0 ? { trimStartMs: nonNegative(video.trimStartMs) } : {}),
      ...(nonNegative(video.trimFromEndMs) > 0 ? { trimFromEndMs: nonNegative(video.trimFromEndMs) } : {}),
    });
  }
  return holds;
}

/** Epoch ms when the clip finishes. `startAt + startDelay + (duration - trimStart - trimFromEnd)`. */
export function videoEndsAtMs(video: SequenceHoldVideo, startAtMs: number): number | undefined {
  if (video.durationMs == null || !Number.isFinite(startAtMs)) return undefined;
  const playable = video.durationMs - (video.trimStartMs ?? 0) - (video.trimFromEndMs ?? 0);
  if (!Number.isFinite(playable) || playable <= 0) return undefined;
  return startAtMs + video.startDelayMs + playable;
}

/**
 * Epoch ms until auto-loop may leave this frame. At least one dwell. Longer when a video that ends
 * on this frame is still playing. The start time is the stamp of the step that started the clip.
 */
export function sequenceStepHoldUntilMs(input: {
  dwellMs: number;
  frameEnteredAtMs: number;
  nowMs: number;
  frameIndex: number;
  videos: SequenceHoldVideo[];
  enteredAtByFrame: ReadonlyMap<number, number>;
}): number {
  const dwellUntil = input.frameEnteredAtMs + input.dwellMs;
  const blocking = videosCutByLeavingFrame(input.videos, input.frameIndex);
  if (blocking.length === 0) return dwellUntil;

  let until = dwellUntil;
  for (const video of blocking) {
    const startFrame = video.startFrame ?? input.frameIndex;
    const startAt = input.enteredAtByFrame.get(startFrame);
    if (startAt == null) continue;
    const endsAt = videoEndsAtMs(video, startAt);
    if (endsAt == null) continue;
    const capped = Math.min(endsAt, input.frameEnteredAtMs + SEQUENCE_VIDEO_HOLD_CAP_MS);
    if (capped > until) until = capped;
  }
  return until;
}

/**
 * Coming back to a frame is a new entry. A different deck starts again.
 * Staying on the same frame does not replace its stamp.
 */
export function rememberSequenceFrameEntry(
  history: SequenceFrameHistory,
  mediaKey: string,
  index: number,
  nowMs: number,
): void {
  if (history.mediaKey !== mediaKey) {
    history.mediaKey = mediaKey;
    history.lastIndex = undefined;
    history.byFrame = new Map();
    history.dwellRestart = undefined;
  }
  if (history.lastIndex !== index) {
    history.byFrame.set(index, nowMs);
    history.lastIndex = index;
    history.dwellRestart = undefined;
  }
}

/** Play after a pause starts this frame's dwell again. Clip start times already stored stay. */
export function restartSequenceFrameDwell(history: SequenceFrameHistory, index: number, nowMs: number): void {
  if (history.lastIndex !== index || !history.byFrame.has(index)) return;
  history.dwellRestart = { index, at: nowMs };
}

/**
 * Opening after a clip's start frame still starts that clip now (`startNow`). Record the step
 * time that makes the hold end when that playback ends. A start frame already visited stays.
 */
export function rememberLocalClipStart(
  history: SequenceFrameHistory,
  mediaKey: string,
  frameIndex: number,
  nowMs: number,
  videos: SequenceHoldVideo[],
): void {
  if (history.mediaKey && history.mediaKey !== mediaKey) return;
  if (!history.mediaKey) history.mediaKey = mediaKey;
  for (const video of videos) {
    const startFrame = video.startFrame;
    if (startFrame == null || frameIndex <= startFrame || frameIndex > video.toFrame) continue;
    if (history.byFrame.has(startFrame)) continue;
    history.byFrame.set(startFrame, nowMs - video.startDelayMs);
  }
}

export type PreviewStepWindow = {
  enteredAtMs: number;
  holdUntilMs: number;
  videoStartedAt: Record<string, number>;
};

/** Local preview clock: the preview owns the stamps and the bar fills this window. */
export function localPreviewStepWindow(input: {
  dwellMs: number;
  frameIndex: number;
  nowMs: number;
  videoOverlays?: string;
  history: SequenceFrameHistory;
  mediaKey: string;
}): PreviewStepWindow {
  rememberSequenceFrameEntry(input.history, input.mediaKey, input.frameIndex, input.nowMs);
  const videos = parseSequenceHoldVideos(input.videoOverlays);
  rememberLocalClipStart(input.history, input.mediaKey, input.frameIndex, input.nowMs, videos);
  const frameEnteredAtMs = input.history.byFrame.get(input.frameIndex) ?? input.nowMs;
  const dwellRestart = input.history.dwellRestart;
  const enteredAtMs = dwellRestart?.index === input.frameIndex ? dwellRestart.at : frameEnteredAtMs;
  const holdUntilMs = sequenceStepHoldUntilMs({
    dwellMs: input.dwellMs,
    frameEnteredAtMs: enteredAtMs,
    nowMs: input.nowMs,
    frameIndex: input.frameIndex,
    videos,
    enteredAtByFrame: input.history.byFrame,
  });
  const videoStartedAt: Record<string, number> = {};
  for (const video of videos) {
    if (video.startFrame == null || input.frameIndex < video.startFrame || input.frameIndex > video.toFrame) continue;
    const started = input.history.byFrame.get(video.startFrame);
    if (started != null) videoStartedAt[video.id] = started;
  }
  return { enteredAtMs, holdUntilMs, videoStartedAt };
}

/**
 * Local catch-up when the next sequence event has not arrived. The new frame gets its own
 * entry time. A clip that continues keeps the start already published. A clip that starts
 * here starts now. The hold is the same video-aware rule the players use.
 */
export function extrapolateLiveAutoLoopStep(input: {
  dwellMs: number;
  nextIndex: number;
  enteredAtMs: number;
  nowMs: number;
  videoOverlays?: string;
  previousVideoStartedAt?: Record<string, number>;
}): { enteredAt: number; holdUntil: number; videoStartedAt: Record<string, number> } {
  const videos = parseSequenceHoldVideos(input.videoOverlays);
  const enteredAtByFrame = new Map<number, number>([[input.nextIndex, input.enteredAtMs]]);
  const videoStartedAt: Record<string, number> = {};
  for (const video of videos) {
    if (video.startFrame == null || input.nextIndex < video.startFrame || input.nextIndex > video.toFrame) {
      continue;
    }
    const started = video.startFrame === input.nextIndex ? input.enteredAtMs : input.previousVideoStartedAt?.[video.id];
    if (started == null) {
      continue;
    }
    if (!enteredAtByFrame.has(video.startFrame)) {
      enteredAtByFrame.set(video.startFrame, started);
    }
    videoStartedAt[video.id] = enteredAtByFrame.get(video.startFrame) ?? started;
  }
  const holdUntil = sequenceStepHoldUntilMs({
    dwellMs: input.dwellMs,
    frameEnteredAtMs: input.enteredAtMs,
    nowMs: input.nowMs,
    frameIndex: input.nextIndex,
    videos,
    enteredAtByFrame,
  });
  return { enteredAt: input.enteredAtMs, holdUntil, videoStartedAt };
}

export type LiveStepTiming = {
  index: number;
  enteredAt: number;
  holdUntil?: number;
  videoStartedAt?: Record<string, number>;
};

/**
 * Live preview clock. Prefer the sequence-index event. The layer stamps cover the first paint
 * before that event. Missing hold falls back to one dwell from the step start.
 */
export function livePreviewStepWindow(input: {
  dwellMs: number;
  frameIndex: number;
  store?: LiveStepTiming;
  layerChangedAt?: string;
  layerHoldUntil?: string;
  layerCurrentIndex?: string;
  layerVideoAnchors?: string;
}): PreviewStepWindow | null {
  const fromStore = input.store != null && input.store.index === input.frameIndex ? input.store : undefined;
  const layerIndex = input.layerCurrentIndex ? Number.parseInt(input.layerCurrentIndex, 10) : input.frameIndex;
  const layerEntered = positiveMs(input.layerChangedAt);
  const fromLayer =
    fromStore == null && layerEntered != null && layerIndex === input.frameIndex
      ? {
          enteredAt: layerEntered,
          holdUntil: positiveMs(input.layerHoldUntil),
          videoStartedAt: parseVideoAnchors(input.layerVideoAnchors),
        }
      : undefined;
  const enteredAtMs = fromStore?.enteredAt ?? fromLayer?.enteredAt;
  if (enteredAtMs == null || !(enteredAtMs > 0)) return null;
  const explicitHold = fromStore?.holdUntil ?? fromLayer?.holdUntil;
  const holdUntilMs = explicitHold != null && explicitHold > enteredAtMs ? explicitHold : enteredAtMs + input.dwellMs;
  return {
    enteredAtMs,
    holdUntilMs,
    videoStartedAt: fromStore?.videoStartedAt ?? fromLayer?.videoStartedAt ?? {},
  };
}

/**
 * Start time for each embedded video in Current playout.
 * A clip that starts on this step uses the same step time as the players.
 * A published per-clip time wins. A layer time is used only before any socket
 * step exists, so a jump does not play the previous visit.
 */
export function livePreviewVideoStarts(input: {
  frameIndex: number;
  videos: Array<{ id: string; startFrame?: number }>;
  store?: LiveStepTiming;
  layerChangedAt?: string;
  layerCurrentIndex?: string;
  layerVideoAnchors?: string;
}): Record<string, number> | undefined {
  const fromStore = input.store != null && input.store.index === input.frameIndex ? input.store : undefined;
  const layerIndex = input.layerCurrentIndex ? Number.parseInt(input.layerCurrentIndex, 10) : Number.NaN;
  const layerEntered = positiveMs(input.layerChangedAt);
  const fromLayer =
    input.store == null && layerEntered != null && layerIndex === input.frameIndex
      ? { enteredAt: layerEntered, videoStartedAt: parseVideoAnchors(input.layerVideoAnchors) }
      : undefined;
  if (input.store != null && fromStore == null) {
    return {};
  }
  const source = fromStore ?? fromLayer;
  if (!source) return undefined;
  const starts: Record<string, number> = { ...(source.videoStartedAt ?? {}) };
  for (const video of input.videos) {
    if (starts[video.id] != null) continue;
    if (video.startFrame === input.frameIndex && source.enteredAt > 0) {
      starts[video.id] = source.enteredAt;
    }
  }
  return starts;
}

/** 0 at the step start, 1 when the step may advance. */
export function sequenceStepProgress(nowMs: number, enteredAtMs: number, holdUntilMs: number): number {
  const span = holdUntilMs - enteredAtMs;
  if (!(span > 0)) return 1;
  return Math.min(1, Math.max(0, (nowMs - enteredAtMs) / span));
}

/** Milliseconds left before a live clip may start. A deadline already passed is zero. */
export function previewVideoStartDelayMs(input: {
  startedAtMs: number;
  startDelayMs: number;
  serverNowMs: number;
}): number {
  return Math.max(0, input.startedAtMs + input.startDelayMs - input.serverNowMs);
}

/** Seconds into the file. A missing start stamp stays at the trim start. */
export function previewVideoSeekSec(input: {
  startedAtMs?: number;
  serverNowMs: number;
  startDelayMs: number;
  trimStartMs: number;
  trimFromEndMs: number;
  durationMs?: number;
}): number {
  const trimStartSec = input.trimStartMs / 1000;
  if (input.startedAtMs == null) return trimStartSec;
  const elapsedMs = input.serverNowMs - input.startedAtMs - input.startDelayMs;
  if (elapsedMs <= 0) return trimStartSec;
  const playable =
    input.durationMs != null && input.durationMs > 0
      ? input.durationMs - input.trimStartMs - input.trimFromEndMs
      : undefined;
  const into = playable != null && playable > 0 ? Math.min(elapsedMs, playable) : elapsedMs;
  return (input.trimStartMs + Math.max(0, into)) / 1000;
}

function positiveMs(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function parseVideoAnchors(raw: string | undefined): Record<string, number> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const anchors: Record<string, number> = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) anchors[id] = value;
    }
    return anchors;
  } catch {
    return {};
  }
}

function nonNegative(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}
